import { ArrowLeft, CalendarPlus, Megaphone, Settings, Shield } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { ChipSelect, EmptyState, Field, HexAvatar, Loading, SessionCard, ShareButton } from '../components';
import { formatRelative } from '../format';
import { Link, useRouter } from '../router';
import { useApp } from '../store';
import type { GuildDetailPayload } from '../types';

function GuildSettings({ data, onSaved }: { data: GuildDetailPayload; onSaved: () => void }) {
  const { toast } = useApp();
  const [name, setName] = useState(data.guild.name);
  const [tag, setTag] = useState(data.guild.tag ?? '');
  const [description, setDescription] = useState(data.guild.description);
  const [joinPolicy, setJoinPolicy] = useState(data.guild.joinPolicy);
  const [discordInviteUrl, setDiscordInviteUrl] = useState(data.guild.discordInviteUrl ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.updateGuild(data.guild.slug, { name, tag, description, joinPolicy, discordInviteUrl });
      toast('Guild settings saved.', 'success');
      onSaved();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save settings.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack-form edit-form" onSubmit={submit}>
      <div className="field-row">
        <Field label="Name"><input value={name} onChange={(event) => setName(event.target.value)} minLength={3} maxLength={50} required /></Field>
        <Field label="Tag"><input value={tag} onChange={(event) => setTag(event.target.value.toUpperCase())} maxLength={5} /></Field>
      </div>
      <Field label="Description"><textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} rows={2} /></Field>
      <Field label="Joining">
        <ChipSelect
          options={[
            { value: 'open', label: 'Open' },
            { value: 'apply', label: 'Apply to join' },
            { value: 'invite', label: 'Invite only' }
          ]}
          selected={[joinPolicy]}
          onToggle={setJoinPolicy}
          single
        />
      </Field>
      <Field label="Discord invite (optional)" hint="A discord.gg link members can use for voice and chat.">
        <input value={discordInviteUrl} onChange={(event) => setDiscordInviteUrl(event.target.value)} placeholder="https://discord.gg/…" />
      </Field>
      <button className="primary small" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
    </form>
  );
}

function PendingRequests({ slug, onChange }: { slug: string; onChange: () => void }) {
  const { toast } = useApp();
  const [pending, setPending] = useState<Array<{ userId: string; username: string; requestedAt: string }> | null>(null);

  const load = useCallback(() => {
    api.guildPending(slug).then((data) => setPending(data.pending)).catch(() => setPending([]));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (!pending || pending.length === 0) return null;

  async function act(userId: string, action: 'approve' | 'deny') {
    try {
      await api.guildMemberAction(slug, userId, action);
      toast(action === 'approve' ? 'Member approved.' : 'Request denied.', 'success');
      load();
      onChange();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'That did not work.', 'error');
    }
  }

  return (
    <section aria-label="Join requests">
      <div className="section-heading slim"><h2>Join requests</h2></div>
      {pending.map((request) => (
        <div key={request.userId} className="roster-row">
          <Link to={`/players/${request.username}`} className="host-line">{request.username}</Link>
          <span className="roster-tags">
            <span className="mono muted">{formatRelative(request.requestedAt)}</span>
            <button className="secondary small" onClick={() => act(request.userId, 'approve')}>Approve</button>
            <button className="ghost small" onClick={() => act(request.userId, 'deny')}>Deny</button>
          </span>
        </div>
      ))}
    </section>
  );
}

export function GuildDetail({ slug }: { slug: string }) {
  const { authStatus, profile, user, toast } = useApp();
  const { navigate } = useRouter();
  const [data, setData] = useState<GuildDetailPayload | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [postBody, setPostBody] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const justCreated = new URLSearchParams(window.location.search).get('created') === '1';

  const load = useCallback(() => {
    api.guild(slug).then(setData).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (missing) {
    return (
      <div className="page narrow">
        <h1>Guild not found</h1>
        <Link to="/guilds" className="secondary small">All guilds</Link>
      </div>
    );
  }
  if (!data) return <Loading label="Loading guild…" />;

  const { guild, members, posts, sessions, viewer } = data;
  const isMember = viewer.role !== null;

  async function join() {
    setBusy(true);
    try {
      const result = await api.joinGuild(slug);
      toast(result.status === 'active' ? `Welcome to ${guild.name}!` : 'Request sent — an officer will review it.', 'success');
      load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not join.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    if (!window.confirm(viewer.role === 'owner' ? 'You are the owner — leaving disbands the guild if you are the last member. Continue?' : `Leave ${guild.name}?`)) return;
    setBusy(true);
    try {
      const result = await api.leaveGuild(slug);
      if (result.disbanded) {
        toast('Guild disbanded.');
        navigate('/guilds');
        return;
      }
      toast(`You left ${guild.name}.`);
      load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not leave.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function submitPost(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.postGuildAnnouncement(slug, postBody);
      setPostBody('');
      toast('Announcement posted — members are notified.', 'success');
      load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not post.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function memberAction(userId: string, action: string, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await api.guildMemberAction(slug, userId, action);
      toast('Done.', 'success');
      load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'That did not work.', 'error');
    }
  }

  return (
    <div className="page narrow">
      <Link to="/guilds" className="back-link"><ArrowLeft size={15} /> All guilds</Link>

      <div className="profile-head">
        <span className="guild-hex" aria-hidden="true"><Shield size={26} /></span>
        <div>
          <h1>{guild.name}</h1>
          <p className="muted">
            {guild.tag && <span className="mono">[{guild.tag}]</span>} {members.length} member{members.length === 1 ? '' : 's'} ·{' '}
            {guild.joinPolicy === 'open' ? 'Open to everyone' : guild.joinPolicy === 'apply' ? 'Apply to join' : 'Invite only'}
          </p>
        </div>
      </div>

      {guild.description && <p className="detail-description">{guild.description}</p>}

      {justCreated && (
        <div className="share-callout">
          <div>
            <strong>Guild created 🎉</strong>
            <small>Share it to recruit members and grow your roster.</small>
          </div>
          <ShareButton url={`/guilds/${guild.slug}`} title={guild.name} text={`Join ${guild.name} on TeamCraft`} label="Share guild" variant="primary small" />
        </div>
      )}

      <div className="action-bar">
        {authStatus === 'authed' && !isMember && !viewer.pending && guild.joinPolicy !== 'invite' && (
          <button className="primary" disabled={busy} onClick={join}>{guild.joinPolicy === 'open' ? 'Join guild' : 'Apply to join'}</button>
        )}
        {viewer.pending && <span className="pill">Request pending</span>}
        {authStatus === 'guest' && <Link to="/signup" className="primary">Create an account to join</Link>}
        <ShareButton url={`/guilds/${guild.slug}`} title={guild.name} text={`Join ${guild.name} on TeamCraft`} variant="secondary" />
        {isMember && (
          <Link to={`/sessions/new?guild=${guild.slug}`} className="secondary"><CalendarPlus size={15} /> Host guild session</Link>
        )}
        {guild.discordInviteUrl && isMember && (
          <a className="ghost" href={guild.discordInviteUrl} target="_blank" rel="noreferrer">Discord</a>
        )}
        {viewer.canManageGuild && (
          <button className="ghost" onClick={() => setShowSettings((current) => !current)}><Settings size={15} /> Settings</button>
        )}
        {isMember && viewer.role !== 'owner' && <button className="ghost danger" disabled={busy} onClick={leave}>Leave</button>}
        {viewer.role === 'owner' && members.length === 1 && <button className="ghost danger" disabled={busy} onClick={leave}>Disband</button>}
      </div>

      {showSettings && viewer.canManageGuild && <GuildSettings data={data} onSaved={() => { setShowSettings(false); load(); }} />}

      {viewer.canManageMembers && <PendingRequests slug={slug} onChange={load} />}

      <section aria-label="Upcoming guild sessions">
        <div className="section-heading slim"><h2>Upcoming sessions</h2></div>
        {sessions.length === 0 ? (
          <EmptyState
            title="Nothing scheduled"
            hint={isMember ? 'Host a session for the guild — it shows up here and on members’ boards.' : 'Members will see guild sessions here.'}
            action={isMember ? <Link to={`/sessions/new?guild=${guild.slug}`} className="primary small">Host guild session</Link> : undefined}
          />
        ) : (
          <div className="session-grid">
            {sessions.map((session) => <SessionCard key={session.id} session={session} profile={profile} />)}
          </div>
        )}
      </section>

      <section aria-label="Announcements">
        <div className="section-heading slim">
          <h2><Megaphone size={16} /> Announcements</h2>
        </div>
        {viewer.canPost && (
          <form className="stack-form" onSubmit={submitPost}>
            <Field label="Post to the guild">
              <textarea value={postBody} onChange={(event) => setPostBody(event.target.value)} maxLength={1000} rows={2} placeholder="Raid night moves to Thursday this week…" required />
            </Field>
            <button className="secondary small" type="submit" disabled={busy}>Post announcement</button>
          </form>
        )}
        {posts.length === 0 ? (
          <p className="muted">No announcements yet.</p>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="post-row">
              <p>{post.body}</p>
              <div className="post-meta">
                <span className="mono muted">{post.authorUsername} · {formatRelative(post.createdAt)}</span>
                {viewer.canPost && (
                  <button className="ghost small" onClick={() => { if (window.confirm('Delete this announcement?')) void api.deleteGuildPost(slug, post.id).then(load); }}>Delete</button>
                )}
              </div>
            </div>
          ))
        )}
      </section>

      <section aria-label="Members">
        <div className="section-heading slim"><h2>Members</h2></div>
        {members.map((member) => (
          <div key={member.userId} className="roster-row">
            <Link to={`/players/${member.username}`} className="host-line">
              <HexAvatar label={member.avatar} size="sm" />{member.displayName}
            </Link>
            <span className="roster-tags">
              <span className={`pill ${member.role !== 'member' ? 'mint' : ''}`}>{member.role}</span>
              {viewer.canManageMembers && member.userId !== user?.id && member.role === 'member' && (
                <>
                  {viewer.role === 'owner' && (
                    <button className="ghost small" onClick={() => memberAction(member.userId, 'promote')}>Promote</button>
                  )}
                  <button className="ghost small" onClick={() => memberAction(member.userId, 'remove', `Remove ${member.displayName} from the guild?`)}>Remove</button>
                </>
              )}
              {viewer.role === 'owner' && member.userId !== user?.id && member.role === 'officer' && (
                <>
                  <button className="ghost small" onClick={() => memberAction(member.userId, 'demote')}>Demote</button>
                  <button className="ghost small" onClick={() => memberAction(member.userId, 'transfer-ownership', `Make ${member.displayName} the owner of ${guild.name}? You become an officer.`)}>Make owner</button>
                </>
              )}
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}
